import classnames from "classnames";
import * as I from "immutable";
import moment from "moment-timezone";
import Raven from "raven-js";
import React, {Component} from "react";
import PropTypes from "prop-types";

import {DATE_FORMAT_ISO, DEFAULT_MANPOWER} from "config/constants";
import {trackEvent} from "metrics";
import {getDateFormat, isDataLoaded} from "utils";

import api from "v2/api";
import {apiAssignee} from "v2/api/Assignees";
import {apiError} from "v2/api/Errors";
import {AppData} from "v2/api/Records";

import AssignmentsSelect from "v2/components/AssignmentsSelect";
import DatePicker from "v2/components/DatePicker";
import FieldError from "v2/components/FieldError";
import FieldGroup from "v2/components/FieldGroup";
import ServerError from "v2/components/ServerError";

import ModalForm from "v2/components/responsive/ModalForm";

export default class AddRouteForm extends Component {
  static contextTypes = {
    appData: PropTypes.instanceOf(AppData),
    location: PropTypes.object,
    params: PropTypes.shape({
      date: PropTypes.string.isRequired,
    }),
    onUpdateDateData: PropTypes.func,
    router: PropTypes.object,
  }
  static defaultProps = {
    fieldContainerClassName: "col-xs-12 col-sm-9",
    labelClassName: "col-xs-12 col-sm-3 control-label",
  }
  static propTypes = {
    fieldContainerClassName: PropTypes.string,
    labelClassName: PropTypes.string,
    modal: PropTypes.bool,
    onCancel: PropTypes.func.isRequired,
  }

  constructor(props, context) {
    super(props, context);

    const {appData: {serviceType}, params: {date}} = context;

    this.state = {
      assignments: I.List(),
      data: I.Map(),
      dataError: I.Map(),
      date: moment(date),
      errors: I.Map(),
      manpower: DEFAULT_MANPOWER,
      submitted: false,
      type: serviceType.keySeq().first(),
    };
  }

  componentDidMount() {
    this.loadData();
  }

  handleAssignmentsChange = (value) => {
    this.setState({
      assignments: I.List(value),
    });
  }

  handleDateChange = (evt, day) => {
    this.setState({
      date: moment(day),
      loadStatus: null,
    }, this.loadData);
  }

  handleManpowerChange = (evt) => {
    this.setState({
      manpower: evt.target.value,
    });
  }

  handleSubmit = (evt) => {
    evt.preventDefault();
    this.setState({
      submitted: true,
    });

    const {location: {query}, onUpdateDateData, router} = this.context;
    const {modal, onCancel} = this.props;
    const {assignments, date, manpower, type} = this.state;
    const dateStr = date.format(DATE_FORMAT_ISO);

    api()
      .post(this.apiUrl, {
        assignments: assignments.map(item => item.value),
        date: dateStr,
        kind: type,
        manpower,
      }, {
        validateStatus: (status) => status === 201,
      })
      .then(({data}) => {
        onUpdateDateData(null, () => {
          if (modal) {
            onCancel();
          }
          trackEvent("Add Route", ["Route", "add", data.id], {
            Date: dateStr,
            ID: data.id,
            Manpower: manpower,
            Type: type,
          });
          router.push({
            pathname: `/${dateStr}/${data.id}`,
            query: {
              ...query,
            },
          });
        });
      })
      .catch((err) => {
        if (err.response) {
          this.setState({
            errors: I.Map(err.response.data),
            submitted: false,
          });
        } else {
          Raven.captureException(err);
        }
      });
  }

  handleTypeChange = (evt) => {
    this.setState({
      type: evt.target.value,
    });
  }

  apiUrl = "/routes"

  loadData() {
    const {date} = this.state;
    const dateStr = date.format(DATE_FORMAT_ISO);

    api()
      .request({
        method: "options",
        url: this.apiUrl,
        params: {
          date: dateStr,
        },
      })
      .then((response) => {
        this.setState(({data}) => ({
          data: data.set(dateStr,
            I.List(response.data.assignments.map(apiAssignee))),
        }));
      })
      .catch((err) => {
        if (err.response) {
          this.setState(({dataError}) => ({
            dataError: dataError.set(dateStr, apiError(err.response.data)),
          }));
        } else {
          Raven.captureException(err);
        }
      });
  }

  renderContent() {
    const {appData: {serviceType}} = this.context;
    const {fieldContainerClassName, labelClassName} = this.props;
    const {
      assignments,
      data,
      dataError,
      date,
      errors,
      manpower,
      type,
    } = this.state;

    const dateStr = date.format(DATE_FORMAT_ISO);
    const dateError = dataError.get(dateStr);

    if (dateError) {
      return <ServerError data={dateError} />;
    }

    let nonFieldErrors;
    if (errors.get("nonFieldErrors")) {
      nonFieldErrors = (
        <div className={classnames(fieldContainerClassName, "col-sm-offset-3", "has-error")}>
          <FieldError>{errors.get("nonFieldErrors")}</FieldError>
        </div>
      );
    }

    return (
      <div className="form-horizontal">
        <FieldGroup error={errors.get("date")}>
          <label className={labelClassName}>Date:</label>
          <div className={fieldContainerClassName}>
            <DatePicker
              linkClassName="erg-inline-block form-control-static"
              onDayClick={this.handleDateChange}
              popover
              value={date.toDate()}>
              {date.format(getDateFormat())}
              <span className="caret erg-margin-left-5" />
            </DatePicker>
            <FieldError>{errors.get("date")}</FieldError>
          </div>
        </FieldGroup>

        <FieldGroup error={errors.get("kind")}>
          <label className={labelClassName} htmlFor="type">Type:</label>
          <div className={fieldContainerClassName}>
            <select
              className="form-control"
              id="type"
              onChange={this.handleTypeChange}
              value={type}>
              {serviceType.map((value, key) => (
                <option key={key} value={key}>{value}</option>
              )).toArray()}
            </select>
            <FieldError>{errors.get("kind")}</FieldError>
          </div>
        </FieldGroup>

        <AssignmentsSelect
          data={data.get(dateStr)}
          error={errors.get("assignments")}
          fieldContainerClassName={fieldContainerClassName}
          focus
          labelClassName={labelClassName}
          onChange={this.handleAssignmentsChange}
          selected={assignments} />

        <FieldGroup error={errors.get("manpower")}>
          <label className={labelClassName}>Manpower:</label>
          <div className={fieldContainerClassName}>
            <input
              className="form-control"
              onChange={this.handleManpowerChange}
              type="text"
              value={manpower} />
            <FieldError>{errors.get("manpower")}</FieldError>
          </div>
        </FieldGroup>
        {nonFieldErrors}
      </div>
    );
  }

  render() {
    const {date, submitted} = this.state;
    const dateStr = date.format(DATE_FORMAT_ISO);
    return (
      <ModalForm
        btnSubmitDisabled={submitted || !isDataLoaded(this.state, dateStr)}
        btnSubmitLabel="Create Route"
        modalTitle="New Route"
        onSubmit={this.handleSubmit}
        pageTitle="New Route"
        {...this.props}>
        {this.renderContent()}
      </ModalForm>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/app/v2/components/responsive/AddRouteForm.js
 **/